<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="form.id">{{ $t('users.edit_user') }} </h1>
      <h1 v-else>{{ $t('users.new_user') }} </h1>
    </div>

    <div class="details-component__content content">
      <form class="form">
        <fieldset class="form__fieldset">
          <div class="form__field" v-if="isLandlord()" :class="{'form__field_error' : errors.has('tenant')}">
            <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
            <el-select name="tenant"
                  v-model="tenant"
                  v-validate="'validTenant'"
                  :data-vv-as="$t('common.tenant')"
                  v-if="tenants.items"
                  placeholder="">
                <el-option
                  v-for="tenant in tenants.items"
                  :key="tenant.id"
                  :label="tenant.name"
                  :value="tenant.id">
                </el-option>
              </el-select>
            <span v-else-if="selectedUser.partner">{{ selectedUser.partner.name }}</span>
            <tooltip v-show="errors.has('tenant')" class="form__validation">{{ $t('errors.tenant_field_required') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('name')}">
            <label for="name" class="form__label">{{ $t('common.name') }}</label>
            <input type="text" class="form__input" name="name" id="name" v-model="form.name" v-validate="'required|max:255'" :data-vv-as="$t('common.name')" data-vv-delay="1000"  />
            <tooltip v-show="errors.has('name')" class="form__validation">{{ $t('errors.name_field') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('mobile')}">
            <label for="mobile" class="form__label">{{ $t('common.phone') }}</label>
            <input type="text" class="form__input" name="mobile" id="mobile" value="" v-model.trim="form.mobile" v-validate="'validMobile'" :data-vv-as="$t('common.phone')" data-vv-delay="1000"  />
            <tooltip v-show="errors.has('mobile')" class="form__validation">{{ $t('errors.phone_field') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('email')}">
            <label for="email" class="form__label">{{ $t('common.email') }}</label>
            <input type="email" class="form__input" name="email" id="email" value="" v-model="form.email" v-validate="'required|email|max:255'" :data-vv-as="$t('common.email')" data-vv-delay="2000"  />
            <tooltip v-show="errors.has('email')" class="form__validation">{{ $t('errors.email_field') }}</tooltip>
          </div>

          <!-- Cars -->
          <div class="form__field" :class="{'form__field_error' : errors.has('vehicleReg'), 'form__field_radiobuttons' : hasVehicles}">

            <label for="vehicleReg" class="form__label">{{ $tc('common.user_vehicle_reg', 'vehicleReg') }}</label>

            <div class="radiobuttons">
              <template v-for="vehicle in vehicles" v-if="!isNew">
                <div class="radiobuttons__element" ref="vehicle">
                  <span :for="vehicle.id" class="radiobuttons__description">{{ vehicle.vehicleReg }}</span>
                  <a class="link link--orange" @click="removeVehicle(vehicle)">{{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i></a>
                </div>
              </template>
              <label>
                <input type="text" :placeholder="$t('add_permit.add_car')"
                 class="form__input add-vehicle"
                 name="vehicleReg" id="vehicleReg"
                 v-model.trim="form.vehicleReg"
                 :data-vv-as="$t('add_permit.add_car')" ref="vehicleReg"
                  @keydown="stopInputAtDashAndSpace"
                  @keyup="trimVehicleReg()"
                />
                <!-- <button type="button" class="form__input--btn-plus" @click.prevent.stop="addVehicle()">+</button> -->
              </label>
            </div>
          </div>
          <div class="form__field" v-if="isNew">
            <label  class="form__label" style="width: auto">
              <input type="checkbox" id="add_with_permit" name="add_with_permit" class="form__fieldset__checkbox" v-model="addWithPermit">
              <span v-model="addWithPermit" class="form__fieldset__custom-checkbox"></span>
              <span class="custom-checkbox-label">{{ $t('users.add_with_permit') }}</span>
            </label>
          </div>
        </fieldset>
        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'users' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'user', params: { id: $route.params.id} }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
          {{ $t('common.save') }}</button>
        </div>
      </form>
    </div>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ userWarningHeader }}</h1>
      <p slot="body">{{ userWarningBody }}</p>
    </WarningModal>

  </div>
</template>

<script>
import { parse, format, isValidNumber } from 'libphonenumber-js';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import Autocomplete from 'common/Autocomplete';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    Tooltip,
    Autocomplete,
    ConfirmationModal,
    WarningModal,
  },
  data() {
    return {
      form: {},
      tenant: null,
      showConfirmationModal: false,
      showWarningModal: false,
      userWarningBody: null,
      userWarningHeader: null,
      vehicles: null,
      removedVehicles: [],
      addWithPermit: false,
    };
  },
  mounted() {
    this.fetchAllData();
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    hasVehicles() {
      return this.vehicles && this.vehicles.length > 0;
    },
    ...mapGetters({
      selectedUser: 'users/user/user',
      userError: 'users/user/error',
      usersLimit: 'landlord/users/limit',
      usersKeywordFilter: 'landlord/users/keywordFilter',
      user: 'user/user/user',
      tenants: 'landlord/tenants/tenants',
      vehicleError: 'users/vehicles/error',
    }),
  },
  watch: {
    $route: 'fetchAllData',
  },
  created() {
    this.$store.commit(`users/user/${mTypes.CLEAR_STATE}`);
    this.resetValidators();
    this.createValidators();
  },
  methods: {
    trimVehicleReg() {
      if (this.form.vehicleReg) {
        this.form.vehicleReg = this.form.vehicleReg.replace('-', '').replace(' ', '');
      }
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    fetchAllData() {
      if (this.$route.params.id) {
        this.fetchUser();
      }
      if (this.isLandlord()) {
        this.fetchTenants();
      }
    },
    fetchUser() {
      this.$store.dispatch('users/user/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.form = this.selectedUser;
        if (this.selectedUser.partner) {
          this.tenant = this.selectedUser.partner[0].id;
        }
        if (this.selectedUser.vehicles) {
          this.vehicles = this.selectedUser.vehicles;
        }
        this.formatMobileNrForForm();
      });
    },
    fetchTenants() {
      if (this.user.data) {
        this.$store.dispatch('landlord/tenants/list', { user: this.user });
      }
    },
    formatMobileNrForForm() {
      if (this.form.mobile) {
        this.form.mobile = format({ country: this.form.mobileCountry, phone: this.form.mobile.toString() }, 'International_plaintext');
      } else {
        this.form.mobile = '';
      }
    },
    onSubmit() {
      this.$store.commit(`users/vehicles/${mTypes.DATA_ERROR}`, null);
      this.$validator.validateAll(
        { name: this.form.name,
          mobile: this.form.mobile,
          email: this.form.email,
          tenant: 'validTenant' },
      )
        .then((success) => {
          this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, null);
          this.deleteVehicles();
          if (success) {
            this.setCreatedBy();
            this.form.partner_id = this.isLandlord() ? this.tenant : this.user.data.selectedRole.id;
            this.parsePhoneBeforeSave();
            this.$store.dispatch('users/user/saveUser', { selectedUser: this.form, user: this.user })
              .then(() => {
                if (this.userError) {
                  this.userWarningHeader = this.$t('common.warning');
                  this.userWarningBody = this.$t('users.save_error_email');
                  this.showWarningModal = true;
                } else if (this.vehicleError) {
                  this.showWarningModal = true;
                  this.userWarningHeader = this.$t('common.warning');
                  this.userWarningBody = this.$t(`errors.${this.vehicleError.body.error}`);
                  this.vehicles = this.selectedUser.vehicles;
                  this.removedVehicles = [];
                } else {
                  this.resetValidators();
                  this.$store.dispatch('landlord/users/fetch', { user: this.user, limit: this.usersLimit, keyword: this.usersKeywordFilter });
                  if (this.addWithPermit) {
                    this.$router.push({ name: 'add-permit-to-user', params: { userId: this.selectedUser.id } });
                  } else {
                    this.$router.push({ name: 'user', params: { id: this.selectedUser.id } });
                  }
                }
              })
              .catch(() => {
                this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
              });
          } else {
            this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
          }
        })
        .catch(() => {
          this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
        });
    },
    setCreatedBy() {
      this.form.created_by = this.user.data.selectedRole.id;
    },
    parsePhoneBeforeSave() {
      const parsedPhone = parse(this.form.mobile, { country: { default: 'EE' } });
      this.form.mobileCountry = parsedPhone.country;
      this.form.mobile = parsedPhone.phone;
    },
    resetValidators() {
      this.$validator.remove('validMobile');
      this.$validator.remove('validTenant');
      this.$validator.clean();
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    createValidators() {
      this.createPhoneNrValidator();
      this.createTenantValidator();
    },
    createPhoneNrValidator() {
      this.$validator.extend('validMobile', {
        getMessage: () => 'Please insert valid phone number. Mobile number must include country code.',
        validate: (value) => {
          if (value.trim === '') {
            return true;
          }
          const parsed = parse(value, { country: { default: 'EE' } });
          return isValidNumber(parsed);
        },
      });
    },
    createTenantValidator() {
      this.$validator.extend('validTenant', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isLandlord() && (typeof this.tenant !== 'undefined' && this.tenant !== null),
      });
    },
    removeVehicle(vehicle) {
      if (this.form.vehicle && this.form.vehicle.id === vehicle.id) {
        this.form.vehicle = null;
      }
      const index = this.vehicles.findIndex(obj => obj === vehicle);
      this.$delete(this.vehicles, index);
      this.removedVehicles.push(vehicle.id);
    },
    deleteVehicles() {
      this.removedVehicles.forEach((el) => {
        this.$store.dispatch('users/vehicles/delete', { id: el, user: this.user });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';

.form {
  &__selectbox {
    width: 200px;
  }

  .add-vehicle {
    width: 100%;
    font-size: 12px;

    ::placeholder {
      font-size: 12px;
      padding: 15px 0 0 0;
      opacity: 0.4;
      padding: 10px 0;
      font-weight: 700;
    }

    &--btn-plus {
      float: right;
      position: relative;
      bottom: 26px;
      border: none;
      background: none;
      font-size: 17px;
      right: 4px;
      color: var(--brand-blue);
      cursor: pointer;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/users/AddUserView.vue